/**
 * The live voice lane: the conversation runs on a Hooper Buddy voice node
 * (voice-stacks/hooperbuddy-voice, Pipecat) instead of in this tab.
 *
 * The Worker decides who gets it: POST /api/voice/session answers with the
 * node's url and a signed token, and this file turns that into a WebRTC call.
 * The node hears through the call (RNNoise, Silero, Smart Turn on the server),
 * thinks through the same brain as the browser lane (the voice bridge, as this
 * player), and speaks back over it. The tab shows the conversation exactly as
 * the browser lane does, and ends it cleanly.
 *
 * Plain browser WebRTC, no library. The Pipecat client library routed the mic
 * through Daily's media manager, which downloads Daily's call engine from
 * c.daily.co and runs it with eval: the page's security policy refuses both, so
 * every call timed out and fell back. What the node needs is small and is all
 * here:
 *   1. POST {node}/start { hb_token, body: { hb_token } }  -> { sessionId, iceConfig? }
 *   2. an offer with the mic and a data channel, to
 *      POST {node}/sessions/{sessionId}/api/offer            -> the answer
 *   3. on the data channel: "ping" every second (the node counts a peer gone
 *      after three quiet seconds), a client-ready, then RTVI messages back:
 *      bot-ready, the player's transcripts, her words, speaking states.
 * The audio is DTLS-SRTP, as all WebRTC media is; nothing here can turn it off.
 */

const CONNECT_MS = 4000;          // mic granted to her ready; slower than this and the browser lane takes over
const ICE_WAIT_MS = 1200;         // gathering usually ends in a few hundred ms; the offer goes with what there is by then
const PING_MS = 1000;
const RTVI = { label: 'rtvi-ai', version: '2.1.0' };
const MIC = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

const post = async (url, body) => {
  const r = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  if (!r.ok) throw Object.assign(new Error(r.status === 503 ? 'The voice node is full.' : `The voice node answered ${r.status}.`), { status: r.status });
  return r.json();
};

/** Resolve once ICE gathering is complete, or after `ms`, whichever is first. */
const gathered = (pc, ms) => new Promise((resolve) => {
  if (pc.iceGatheringState === 'complete') return resolve();
  const done = () => { clearTimeout(t); pc.removeEventListener('icegatheringstatechange', check); resolve(); };
  const check = () => { if (pc.iceGatheringState === 'complete') done(); };
  const t = setTimeout(done, ms);
  pc.addEventListener('icegatheringstatechange', check);
});

/**
 * Start a session on the node and resolve once the bot is ready to talk.
 *
 * @param url        the node, from /api/voice/session
 * @param token      the signed session token, from the same answer
 * @param onUserText (text) => void, a finished thing the player said
 * @param onPartial  (text) => void, words as they arrive, for the input box
 * @param onBotText  (text, done) => void, her words as they stream; done true carries the whole reply
 * @param onState    (state) => void, one of listening | thinking | talking | off
 * @param onEnd      () => void, the call ended for any reason but stop()
 * @param stream     optional MediaStream to send instead of the mic (tests)
 * @returns { stop() }
 */
export async function connectVoice({ url, token, onUserText, onPartial, onBotText, onState, onEnd, timeoutMs = CONNECT_MS, stream: given = null }) {
  const base = String(url).replace(/\/$/, '');
  let pc = null, dc = null, mic = null, audio = null, ping = null, timer = null;
  let ready = false, ended = false, reply = '', replying = false, pcId = null, sessionId = null;
  const state = (s) => { try { onState?.(s); } catch {} };

  const teardown = () => {
    clearInterval(ping); clearTimeout(timer);
    try { dc?.close(); } catch {}
    try { pc?.close(); } catch {}
    if (mic && mic !== given) mic.getTracks().forEach((t) => t.stop());
    if (audio) { try { audio.pause(); audio.srcObject = null; audio.remove(); } catch {} }
    pc = dc = mic = audio = null;
  };
  const endReply = () => {
    if (!replying) return;
    replying = false;
    const text = reply.trim(); reply = '';
    try { onBotText?.(text, true); } catch {}
  };

  let settle;
  const started = new Promise((resolve, reject) => { settle = { resolve, reject }; });
  const fail = (e) => { if (ready || ended) return; ended = true; teardown(); settle.reject(e instanceof Error ? e : new Error(String(e))); };
  // After it started: the node hung up, the network dropped, or the node said it is leaving.
  const drop = () => {
    if (!ready) return fail(new Error('The voice node hung up before the call started.'));
    if (ended) return;
    ended = true; endReply(); teardown(); state('off');
    try { onEnd?.(); } catch {}
  };
  const handle = { stop() { if (ended) return; ended = true; teardown(); } };

  const send = (msg) => { if (dc?.readyState === 'open') dc.send(JSON.stringify(msg)); };
  const rtvi = (m) => {
    const d = m.data || {};
    switch (m.type) {
      case 'bot-ready':
        if (!ready && !ended) { ready = true; clearTimeout(timer); state('listening'); settle.resolve(handle); }
        break;
      case 'user-started-speaking': state('listening'); break;
      case 'user-stopped-speaking': state('thinking'); break;
      case 'user-transcription': {
        const text = String(d.text || '').trim();
        if (!text) break;
        if (d.final) { try { onUserText?.(text); } catch {} } else { try { onPartial?.(text); } catch {} }
        break;
      }
      case 'bot-llm-started': endReply(); replying = true; reply = ''; break;
      case 'bot-llm-text': { const t = String(d.text || ''); if (!t) break; replying = true; reply += t; try { onBotText?.(t, false); } catch {} break; }
      case 'bot-llm-stopped': endReply(); break;
      case 'bot-started-speaking': state('talking'); break;
      case 'bot-stopped-speaking': endReply(); state('listening'); break;
      // Only a fatal error ends the call. A TTS hiccup on the greeting is
      // reported too, and it must not throw away a call that is otherwise fine.
      case 'error': if (d.fatal) { if (ready) drop(); else fail(new Error('The voice node could not start.')); } break;
      default: break;
    }
  };
  // The node asks for a fresh offer when it wants to renegotiate.
  const renegotiate = async () => {
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const answer = await post(`${base}/sessions/${sessionId}/api/offer`, { sdp: pc.localDescription.sdp, type: 'offer', pc_id: pcId, restart_pc: false, request_data: { hb_token: token } });
      await pc.setRemoteDescription({ type: answer.type, sdp: answer.sdp });
    } catch (e) { console.warn('voice renegotiation failed', e?.message); }
  };

  try {
    // Asked for before the node, so the permission prompt, if there is one,
    // is not counted against the connection's time.
    mic = given || await navigator.mediaDevices.getUserMedia({ audio: MIC });
    if (ended) { teardown(); throw new Error('stopped'); }
    timer = setTimeout(() => fail(new Error(`Live voice did not connect within ${timeoutMs / 1000} s.`)), timeoutMs);

    const start = await post(`${base}/start`, { hb_token: token, body: { hb_token: token } });
    sessionId = start.sessionId;
    if (!sessionId) throw new Error('The voice node did not open a session.');
    if (ended) throw new Error('stopped');

    pc = new RTCPeerConnection({ iceServers: start.iceConfig?.iceServers || [] });
    const [track] = mic.getAudioTracks();
    pc.addTransceiver(track, { direction: 'sendrecv', streams: [mic] });
    pc.ontrack = (e) => {
      if (e.track.kind !== 'audio') return;
      audio ??= Object.assign(document.createElement('audio'), { autoplay: true });
      audio.setAttribute('playsinline', '');
      audio.style.display = 'none';
      if (!audio.isConnected) document.body.append(audio);
      audio.srcObject = e.streams[0] || new MediaStream([e.track]);
      audio.play().catch(() => {});
    };
    pc.onconnectionstatechange = () => { if (['failed', 'closed', 'disconnected'].includes(pc?.connectionState)) drop(); };

    dc = pc.createDataChannel('chat', { ordered: true });
    dc.onopen = () => {
      ping = setInterval(() => { try { dc?.send('ping'); } catch {} }, PING_MS);
      send({ label: RTVI.label, type: 'client-ready', id: crypto.randomUUID(), data: { version: RTVI.version, about: { library: 'hooper-buddy', platform: 'web' } } });
    };
    dc.onclose = () => drop();
    dc.onmessage = (ev) => {
      let m; try { m = JSON.parse(ev.data); } catch { return; }
      if (m?.type === 'signalling') {
        const kind = m.message?.type;
        if (kind === 'renegotiate') renegotiate();
        else if (kind === 'peerLeft') drop();
        return;
      }
      if (m?.label === RTVI.label) rtvi(m);
    };

    await pc.setLocalDescription(await pc.createOffer());
    await gathered(pc, ICE_WAIT_MS);
    if (ended) throw new Error('stopped');
    const answer = await post(`${base}/sessions/${sessionId}/api/offer`, { sdp: pc.localDescription.sdp, type: 'offer', pc_id: null, restart_pc: false, request_data: { hb_token: token } });
    pcId = answer.pc_id || null;
    await pc.setRemoteDescription({ type: answer.type, sdp: answer.sdp });
  } catch (e) {
    fail(e);
  }
  return started;
}
